// WorkExperience.jsx
import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card, CardContent } from '@/components/ui/card';
import { Heading, Text } from '@/components/ui/typography';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

function toDate(d) {
  if (!d) return null;
  if (d instanceof Date) return d;
  return new Date(d);
}
function toISO(d) {
  return d ? d.toISOString() : null;
}

export default function WorkExperience({ onUpdate, data = [] }) {
  const initial = (data || []).map(d => ({
    ...d,
    startDate: toDate(d.startDate),
    endDate: toDate(d.endDate),
    currentlyWorkingHere: !!d.currentlyWorkingHere,
  }));

  const [workExperiences, setWorkExperiences] = useState(() => initial);
  // which index is currently being edited (or null)
  const [editingIndex, setEditingIndex] = useState(null);
  const mountedRef = useRef(false);

  // Sync from parent only when not editing (and avoid stomping on edits)
  useEffect(() => {
    if (!mountedRef.current) {
      setWorkExperiences(initial);
      mountedRef.current = true;
      return;
    }
    if (editingIndex === null) {
      // shallow compare lengths and ids where possible to avoid unnecessary updates
      const incoming = (data || []).map(d => ({
        ...d,
        startDate: toDate(d.startDate),
        endDate: toDate(d.endDate),
        currentlyWorkingHere: !!d.currentlyWorkingHere,
      }));
      // quick equality check (stringified is okay for small arrays)
      if (JSON.stringify(incoming) !== JSON.stringify(workExperiences)) {
        setWorkExperiences(incoming);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editingIndex]);

  // Notify parent when state changes
  useEffect(() => {
    const serialized = workExperiences.map(w => ({
      ...w,
      startDate: toISO(w.startDate),
      endDate: toISO(w.endDate),
      currentlyWorkingHere: !!w.currentlyWorkingHere,
    }));
    onUpdate && onUpdate(serialized);
  }, [workExperiences]); // Removed onUpdate from dependencies to prevent re-render loop

  const handleChange = (index, key, value) => {
    setWorkExperiences(prev => prev.map((item, i) => i === index ? { ...item, [key]: value } : item));
  };

  const handleDateChange = (index, date, field) => {
    setWorkExperiences(prev => prev.map((item, i) => i === index ? { ...item, [field]: date } : item));
  };

  const toggleCurrentlyWorking = (index) => {
    setWorkExperiences(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newVal = !item.currentlyWorkingHere;
      return { ...item, currentlyWorkingHere: newVal, endDate: newVal ? null : item.endDate };
    }));
  };

  const addExperience = () => {
    setWorkExperiences(prev => ([
      ...prev,
      {
        jobTitle: '',
        employmentType: '',
        company: '',
        startDate: null,
        endDate: null,
        location: '',
        description: '',
        currentlyWorkingHere: false,
      }
    ]));
  };

  const deleteExperience = (index) => {
    setWorkExperiences(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className="mb-6 bg-white">
      <CardContent className="p-4">
        <Heading level="h2" className="text-base font-semibold text-foreground mb-3">
          Work Experience
          <span className="text-destructive ml-1">*</span>
        </Heading>

        {workExperiences && workExperiences.length > 0 ? (
          workExperiences.map((experience, index) => (
            <div key={index} className="mb-6 pb-6 border-b border-border last:border-b-0 last:mb-0 last:pb-0">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-foreground mb-1.5">Title</Label>
                  <Input
                    type="text"
                    placeholder="Ex. UI/UX Designer"
                    value={experience.jobTitle ?? ''}
                    onChange={(e) => handleChange(index, 'jobTitle', e.target.value)}
                    onFocus={() => setEditingIndex(index)}
                    onBlur={() => setEditingIndex(null)}
                  />
                </div>

                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-foreground mb-1.5">Employment Type</Label>
                  <Select
                    value={experience.employmentType ?? ''}
                    onValueChange={(value) => handleChange(index, 'employmentType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Employment Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Freelance">Freelance</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Internship">Internship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-foreground mb-1.5">Company</Label>
                  <Input
                    type="text"
                    placeholder="Ex. Company Name"
                    value={experience.company ?? ''}
                    onChange={(e) => handleChange(index, 'company', e.target.value)}
                    onFocus={() => setEditingIndex(index)}
                    onBlur={() => setEditingIndex(null)}
                  />
                </div>

                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-foreground mb-1.5">Location</Label>
                  <Input
                    type="text"
                    placeholder="Ex. Bangalore"
                    value={experience.location ?? ''}
                    onChange={(e) => handleChange(index, 'location', e.target.value)}
                    onFocus={() => setEditingIndex(index)}
                    onBlur={() => setEditingIndex(null)}
                  />
                </div>

                <div className="flex flex-col">
                  <Label className="flex items-center text-sm font-medium text-foreground mb-1.5">
                    <Checkbox
                      checked={!!experience.currentlyWorkingHere}
                      onCheckedChange={() => toggleCurrentlyWorking(index)}
                      className="mr-2.5"
                    />
                    I currently work here
                  </Label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-foreground mb-1.5">Start date</Label>
                  <DatePicker
                    selected={experience.startDate}
                    onChange={(date) => handleDateChange(index, date, 'startDate')}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    className={cn("w-full h-9 px-3 py-1 text-sm border border-input rounded-md bg-background")}
                    placeholderText="Select Start Date"
                    onFocus={() => setEditingIndex(index)}
                  />
                </div>

                {!experience.currentlyWorkingHere && (
                  <div className="flex flex-col">
                    <Label className="text-sm font-medium text-foreground mb-1.5">End date</Label>
                    <DatePicker
                      selected={experience.endDate}
                      onChange={(date) => handleDateChange(index, date, 'endDate')}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className={cn("w-full h-9 px-3 py-1 text-sm border border-input rounded-md bg-background")}
                      placeholderText="Select End Date"
                      onFocus={() => setEditingIndex(index)}
                    />
                  </div>
                )}
              </div>

              <div className="flex flex-col mb-5">
                <Label className="text-sm font-medium text-foreground mb-1.5">Description</Label>
                <Textarea
                  className="min-h-[126px] resize-y"
                  placeholder="Describe your work..."
                  value={experience.description ?? ''}
                  onChange={(e) => handleChange(index, 'description', e.target.value)}
                  onFocus={() => setEditingIndex(index)}
                  onBlur={() => setEditingIndex(null)}
                />
              </div>

              <div className="flex justify-end mt-5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteExperience(index)}
                  className="text-destructive"
                >
                  <X className="w-4 h-4 mr-2" />
                  Delete Experience
                </Button>
              </div>
            </div>
          ))
        ) : (
          <Text variant="muted">No Experience added yet.</Text>
        )}

        <Button type="button" variant="outline" className="w-full mt-4" onClick={addExperience}>
          Add Experience
        </Button>
      </CardContent>
    </Card>
  );
}